import React, { useState, useEffect } from "react";
import { Card, Input, Button, Select, Table, Tag, Space, Tooltip } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { Option } = Select;

const ThemeComponentManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    age_group: "",
    component: "",
    theme: "",
    is_active: true,
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({});

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ageGroupFilter, setAgeGroupFilter] = useState("");
  const [componentFilter, setComponentFilter] = useState("");

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedAgeGroupFilter = useDebounce(ageGroupFilter, 500);
  const debouncedComponentFilter = useDebounce(componentFilter, 500);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Data states
  const [themeManagerList, setThemeManagerList] = useState([]);
  const [themesList, setThemesList] = useState([]);
  const [loadingThemes, setLoadingThemes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Constants
  const AGE_GROUP_OPTIONS = [
    { value: "6-9", label: "6-9 years" },
    { value: "10-14", label: "10-14 years" },
    { value: "15-18", label: "15-18 years" },
  ];

  const COMPONENT_OPTIONS = [
    { value: "WARMINGUP", label: "Warming-up" },
    { value: "SKILLS", label: "Skills" },
    { value: "FUNSTART", label: "Fun-Start" },
    { value: "BALLACTION", label: "Ball-Action" },
    { value: "ATTACK", label: "Attack" },
    { value: "DEFENSE", label: "Defense" },
    { value: "REACTIVESPRINTS", label: "Reactive-sprints" },
  ];

  const COMPONENT_MAPPINGS = {
    "6-9": ["FUNSTART", "SKILLS", "ATTACK", "DEFENSE"],
    "10-14": ["WARMINGUP", "SKILLS", "ATTACK", "DEFENSE"],
    "15-18": ["WARMINGUP", "SKILLS", "ATTACK", "DEFENSE"],
  };

  const getFilteredComponentOptions = (ageGroup) => {
    if (!ageGroup) return [];
    const allowedComponents = COMPONENT_MAPPINGS[ageGroup] || [];
    return COMPONENT_OPTIONS.filter((opt) =>
      allowedComponents.includes(opt.value),
    );
  };

  const STATUS_OPTIONS = [
    { value: "active", label: t("common.active") },
    { value: "inactive", label: t("common.inactive") },
  ];

  // API hooks
  const { execute: fetchList, loading: loadingList } = useGet(
    `${API_ENDPOINTS.THEMES_MANAGER_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}&status=${statusFilter || ""}&age_group=${ageGroupFilter || ""}&component=${componentFilter || ""}`,
  );

  const { post, loading: creating } = usePost(
    API_ENDPOINTS.THEMES_MANAGER_LIST,
  );
  const { execute: getById, loading: loadingById } = useDynamicApi("GET");
  const { execute: putData, loading: updating } = useDynamicApi("PUT");
  const { execute: patchData, loading: updatingStatus } =
    useDynamicApi("PATCH");
  const { execute: deleteData, loading: deleting } = useDynamicApi("DELETE");

  // API for fetching themes list without pagination
  const { execute: fetchThemesList } = useGet(
    API_ENDPOINTS.THEMES_LIST_WITHOUT_PAGINATE,
  );

  // Loading state
  const loading =
    loadingList ||
    loadingById ||
    updating ||
    updatingStatus ||
    deleting ||
    creating ||
    loadingThemes ||
    submitting;

  // Load themes list (for dropdown)
  const loadThemesList = async () => {
    setLoadingThemes(true);
    try {
      const response = await fetchThemesList();
      if (response?.status === true && response.data) {
        setThemesList(response.data);
      } else {
        setThemesList([]);
      }
    } catch (error) {
      console.error("Error loading themes:", error);
      setThemesList([]);
    } finally {
      setLoadingThemes(false);
    }
  };

  // Load theme manager data
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        setThemeManagerList(response.data || []);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    loadData();
    loadThemesList();
  }, [currentPage, pageSize]);

  // Handle filters
  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [debouncedSearchTerm, statusFilter, ageGroupFilter, componentFilter]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle select changes
  const handleSelectChange = (field, value) => {
    if (field === "age_group") {
      setFormData((prev) => ({
        ...prev,
        age_group: value,
        component: undefined,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      age_group: "",
      component: "",
      theme: "",
      is_active: true,
    });
    setFormErrors({});
    setSelectedItem(null);
    setIsEditMode(false);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setAgeGroupFilter("");
    setComponentFilter("");
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.age_group) {
      errors.age_group = "Please select age group";
    }

    if (!formData.component) {
      errors.component = "Please select component";
    }

    if (!formData.theme) {
      errors.theme = "Please select theme";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create
  const handleCreate = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await post(formData);

      if (response?.status === true) {
        Toast.success(
          response?.message || t("themeComponentManager.createdSuccess"),
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(
            response?.message || t("themeComponentManager.failedCreate"),
          );
        }
      }
    } catch (error) {
      console.error("Error creating:", error);
      if (error?.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error?.response?.data?.message) {
        Toast.error(error.response.data.message);
      } else {
        Toast.error(error.message || t("themeComponentManager.failedCreate"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle update
  const handleUpdate = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const endpoint = `${API_ENDPOINTS.THEMES_MANAGER_UPDATE}/${selectedItem.id}`;
      const response = await putData(endpoint, formData);

      if (response?.status === true) {
        Toast.success(
          response?.message || t("themeComponentManager.updatedSuccess"),
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(
            response?.message || t("themeComponentManager.failedUpdate"),
          );
        }
      }
    } catch (error) {
      console.error("Error updating:", error);
      if (error?.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error?.response?.data?.message) {
        Toast.error(error.response.data.message);
      } else {
        Toast.error(error.message || t("themeComponentManager.failedUpdate"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle toggle active status
  const handleToggleActive = async (item) => {
    try {
      const newStatus = !item.is_active;
      const endpoint = `${API_ENDPOINTS.THEMES_MANAGER_UPDATE}/${item.id}`;

      const response = await patchData(endpoint, {
        is_active: newStatus,
      });

      if (response?.status === true) {
        Toast.success(
          response?.message ||
            (newStatus ? t("common.active") : t("common.inactive")),
        );
        await loadData();
      } else {
        Toast.error(response?.message || t("attackManager.failedUpdateStatus"));
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Toast.error(error.message || t("attackManager.failedUpdateStatus"));
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      const endpoint = `${API_ENDPOINTS.THEMES_MANAGER_UPDATE}/${itemToDelete.id}`;
      const response = await deleteData(endpoint);

      if (response?.status === true) {
        Toast.success(
          response?.message || t("themeComponentManager.deletedSuccess"),
        );
        await loadData();
      } else {
        Toast.error(
          response?.message || t("themeComponentManager.failedDelete"),
        );
      }
    } catch (error) {
      console.error("Error deleting:", error);
      Toast.error(error.message || t("themeComponentManager.failedDelete"));
    } finally {
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  // Open modal for create
  const openCreateModal = () => {
    resetForm();
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  // Open modal for edit
  const openEditModal = async (item) => {
    try {
      const endpoint = `${API_ENDPOINTS.THEMES_MANAGER_UPDATE}/${item.id}`;
      const response = await getById(endpoint);

      if (response?.status === true && response.data) {
        setSelectedItem(response.data);
        setFormData({
          age_group: response.data.age_group || "",
          component: response.data.component || "",
          theme: response.data.theme || "",
          is_active: response.data.is_active,
        });
        setIsEditMode(true);
        setIsModalOpen(true);
      } else {
        Toast.error(t("themeComponentManager.failedFetchDetails"));
      }
    } catch (error) {
      console.error("Error fetching details:", error);
      Toast.error(
        error.message || t("themeComponentManager.failedFetchDetails"),
      );
    }
  };

  // Navigate to view page
  const openViewPage = (id) => {
    navigate(`/themecomponent/details/${id}`);
  };

  // Helper functions
  const getAgeGroupLabel = (value) => {
    const option = AGE_GROUP_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value || "N/A";
  };

  const getComponentLabel = (value) => {
    const option = COMPONENT_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value || "N/A";
  };

  const getThemeName = (themeId) => {
    if (!themeId) return "N/A";
    const theme = themesList.find((t) => t.id === themeId);
    return theme ? theme.name : "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasFilters =
    searchTerm || statusFilter || ageGroupFilter || componentFilter;

  // Table columns
  const columns = [
    {
      title: t("common.sno"),
      key: "sno",
      width: 70,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: t("themeComponentManager.ageGroup"),
      dataIndex: "age_group",
      key: "age_group",
      render: (value) => getAgeGroupLabel(value),
    },
    {
      title: t("themeComponentManager.component"),
      dataIndex: "component",
      key: "component",
      render: (value) => getComponentLabel(value),
    },
    {
      title: t("themeComponentManager.title").replace(" Manager", ""),
      dataIndex: "theme",
      key: "theme",
      render: (themeId) => getThemeName(themeId),
    },
    {
      title: t("common.createdAt"),
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => formatDate(date),
    },
    {
      title: t("common.status"),
      dataIndex: "is_active",
      key: "status",
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? "green" : "default"}>
          {isActive ? t("common.active") : t("common.inactive")}
        </Tag>
      ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip
            title={record.is_active ? t("common.inactive") : t("common.active")}
          >
            <Button
              className="btn-toggle-active"
              size="small"
              danger={!record.is_active}
              type={record.is_active ? "default" : "primary"}
              icon={<PoweroffOutlined />}
              onClick={() => handleToggleActive(record)}
              loading={updatingStatus}
            />
          </Tooltip>
          <Tooltip title={t("common.view")}>
            <Button
              className="btn-view"
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewPage(record.id)}
            />
          </Tooltip>
          <Tooltip title={t("common.edit")}>
            <Button
              className="btn-edit"
              type="primary"
              size="small"
              style={{ background: "#52c41a", borderColor: "#52c41a" }}
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <Button
              className="btn-delete"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={t("themeComponentManager.title")}
        extra={
          <Button type="primary" onClick={openCreateModal} disabled={loading}>
            {t("themeComponentManager.createNew")}
          </Button>
        }
      >
        {/* Filters */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <Input
            placeholder={t("common.search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ width: 200 }}
            disabled={loading}
          />

          {/* <Select
            placeholder="Age Group"
            value={ageGroupFilter || undefined}
            onChange={(value) => setAgeGroupFilter(value || "")}
            style={{ width: 150 }}
            allowClear
            disabled={loading}
          >
            {AGE_GROUP_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select> */}

          <Select
            placeholder={t("themeComponentManager.filterByAgeGroup")}
            value={ageGroupFilter || undefined}
            onChange={(value) => setAgeGroupFilter(value || "")}
            style={{ width: 150 }}
            allowClear
            disabled={loading}
          >
            {AGE_GROUP_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>

          <Select
            placeholder={t("themeComponentManager.filterByComponent")}
            value={componentFilter || undefined}
            onChange={(value) => setComponentFilter(value || "")}
            style={{ width: 150 }}
            allowClear
            disabled={loading}
          >
            {COMPONENT_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>

          <Select
            placeholder={t("common.filterByStatus")}
            value={statusFilter || undefined}
            onChange={(value) => setStatusFilter(value || "")}
            style={{ width: 150 }}
            allowClear
            disabled={loading}
          >
            {STATUS_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>

          {hasFilters && (
            <Button onClick={resetFilters} disabled={loading}>
              {t("common.clearSearchToReorder")}
            </Button>
          )}
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={themeManagerList}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50", "100"],
            showTotal: (total) =>
              `${t("common.showing")} ${(currentPage - 1) * pageSize + 1} ${t("common.to")} ${Math.min(currentPage * pageSize, total)} ${t("common.of")} ${total} ${t("common.entries")}`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 10);
            },
          }}
          locale={{
            emptyText: hasFilters
              ? t("common.noMatchingEntries")
              : t("themeComponentManager.noComponentsFound"),
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={
            isEditMode
              ? t("themeComponentManager.editTitle")
              : t("themeComponentManager.createTitle")
          }
          onConfirm={isEditMode ? handleUpdate : handleCreate}
          confirmText={
            isEditMode
              ? submitting
                ? t("themeComponentManager.savingChanges")
                : t("common.update")
              : submitting
                ? t("themeComponentManager.creating")
                : t("common.create")
          }
          confirmDisabled={submitting}
          width={500}
        >
          <form onSubmit={isEditMode ? handleUpdate : handleCreate}>
            {/* Age Group Selection */}
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Age Group <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                // placeholder="Select age group"
                placeholder={t("themeComponentManager.selectAgeGroup")}
                value={formData.age_group || undefined}
                onChange={(value) => handleSelectChange("age_group", value)}
                style={{ width: "100%" }}
                disabled={submitting}
                status={formErrors.age_group ? "error" : ""}
              >
                {AGE_GROUP_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
              {formErrors.age_group && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.age_group}
                </div>
              )}
            </div>

            {/* Component Selection */}
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Component <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                // placeholder={
                //   formData.age_group
                //     ? "Select component"
                //     : "Please select age group first"
                // }
                placeholder={
                  formData.age_group
                    ? t("themeComponentManager.selectComponent")
                    : t("themeComponentManager.selectAgeGroupFirst")
                }
                value={formData.component || undefined}
                onChange={(value) => handleSelectChange("component", value)}
                style={{ width: "100%" }}
                disabled={!formData.age_group || submitting}
                status={formErrors.component ? "error" : ""}
                notFoundContent={
                  !formData.age_group
                    ? "Select age group first"
                    : "No components available"
                }
              >
                {formData.age_group &&
                  getFilteredComponentOptions(formData.age_group).map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
              </Select>
              {formErrors.component && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.component}
                </div>
              )}
              {!formData.age_group && (
                <div
                  style={{
                    color: "#666",
                    fontSize: "12px",
                    marginTop: "5px",
                    fontStyle: "italic",
                  }}
                >
                  Please select an age group first to see available components
                </div>
              )}
            </div>

            {/* Theme Selection */}
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                {t("themeManager.name")} <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                // placeholder="Select theme"
                placeholder={t("themeComponentManager.themeRequired")}
                value={formData.theme || undefined}
                onChange={(value) => handleSelectChange("theme", value)}
                style={{ width: "100%" }}
                disabled={loadingThemes || submitting}
                loading={loadingThemes}
                status={formErrors.theme ? "error" : ""}
                showSearch
                optionFilterProp="children"
              >
                {themesList.map((theme) => (
                  <Option key={theme.id} value={theme.id}>
                    {theme.name}
                  </Option>
                ))}
              </Select>
              {formErrors.theme && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.theme}
                </div>
              )}
            </div>

            {/* Active Status */}
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
                <span>{t("common.active")}</span>
              </label>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && itemToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setItemToDelete(null);
          }}
          title={t("themeComponentManager.confirmDelete")}
          onConfirm={handleDeleteConfirm}
          confirmText={deleting ? t("common.loading") : t("common.delete")}
          confirmColor="danger"
          confirmDisabled={deleting}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>⚠️</div>
            <h3 style={{ marginBottom: "15px" }}>
              {t("themeComponentManager.confirmDelete")}
            </h3>

            <div
              style={{
                textAlign: "left",
                backgroundColor: "#f8f9fa",
                padding: "15px",
                borderRadius: "6px",
                marginBottom: "15px",
              }}
            >
              <p>
                <strong>Age Group:</strong>{" "}
                {getAgeGroupLabel(itemToDelete.age_group)}
              </p>
              <p>
                <strong>Component:</strong>{" "}
                {getComponentLabel(itemToDelete.component)}
              </p>
              <p>
                <strong>{t("themeManager.name")}:</strong>{" "}
                {getThemeName(itemToDelete.theme)}
              </p>
              <p>
                <strong>{t("common.createdAt")}:</strong>{" "}
                {formatDate(itemToDelete.created_at)}
              </p>
              <p>
                <strong>{t("common.status")}:</strong>
                <Tag
                  color={itemToDelete.is_active ? "green" : "default"}
                  style={{ marginLeft: "10px" }}
                >
                  {itemToDelete.is_active
                    ? t("common.active")
                    : t("common.inactive")}
                </Tag>
              </p>
            </div>

            <p style={{ color: "#dc3545" }}>
              {t("themeComponentManager.deleteWarning")}
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ThemeComponentManager;
